/**
 * Cuenta apartada para pagos con varias personas (puro): de quién es cada euro.
 * Quién es quién en sus movimientos, sin tener que marcarlo:
 * - Tuyo: lo que entra desde (o sale hacia) otra cuenta tuya (un traspaso con su pareja en tus cuentas).
 * - De las otras personas: el resto de lo que no es ingreso ni gasto (su nómina, lo que se llevan…).
 * - Común: ingresos y gastos (hipoteca, comunidad, intereses), repartidos con tu % (Propiedad).
 * Tu parte «según lo que aporta cada persona» = saldo inicial × tu % + lo tuyo + tu % de lo común: el dinero de otra persona
 * que pasa por la cuenta (su nómina hasta que se la lleva) nunca mueve tu parte. «Fija» = saldo × tu %.
 */
import { monthRange, prevMonth, round2 } from '@pf/domain';
import type { Computed } from './model.js';
import { accountUse } from './account-use.js';

export type PoolSide = 'mine' | 'others' | 'common';
export interface PoolSummary {
  account: string; purpose: string; people: string[];
  mode: 'fixed' | 'contrib'; share: number;
  /** mine: tu parte de lo que hay (entre 0 y el saldo; es lo que cuenta en tu patrimonio). others: la de las otras personas. */
  balance: number; mine: number; others: number;
  /** Lo que has puesto de más (> 0) o de menos (< 0) frente a tu parte de los pagos: no es dinero de la cuenta, es lo que os debéis. */
  imbalance: number;
  /** Lo aportado en el año (neto: lo que entra menos lo que se saca), tuyo y de las otras personas. */
  contributed: { year: number; mine: number; others: number };
  /** Pagos de la cuenta al mes (media de los 12 meses cerrados con datos) y cuántos meses cubre el saldo. */
  monthlyPayments: number | null; coverMonths: number | null;
}

const countable = (kind: string) => kind === 'income' || kind === 'expense';
export const poolPeople = (raw: string | undefined) => (raw ?? '').split(',').map(x => x.trim()).filter(Boolean);
export const poolMode = (raw: string | undefined): 'fixed' | 'contrib' => (/^aport/i.test((raw ?? '').trim()) ? 'contrib' : 'fixed');

/** De quién es un movimiento de la cuenta. */
export function poolSide(c: Computed, account: string, m: { id: string; account: string; date: string; amount: number; kind: string }): PoolSide {
  if (countable(m.kind)) return 'common';
  const peer = c.movements.some(x => x.id !== m.id && x.account !== account && x.date === m.date && Math.abs(x.amount + m.amount) < 0.005
    && c.wb.accounts.some(a => a.name === x.account));
  return peer ? 'mine' : 'others';
}

/**
 * Tu parte de lo que hay en la cuenta a una fecha (lo que cuenta en tu patrimonio). Si has puesto más que tu parte de los pagos,
 * lo de más no está en la cuenta: lo debe la otra persona, y como lo de Tricount, no cuenta hasta que lo pone (se limita al saldo).
 */
export function poolMineAt(c: Computed, account: string, date: string): number {
  const raw = poolRawMineAt(c, account, date);
  const a = c.wb.accounts.find(x => x.name === account);
  if (!a || poolMode(a.split) === 'fixed') return raw;
  const balance = a.opening + a.archived + c.movements.filter(m => m.account === account && m.date && m.date <= date).reduce((t, m) => t + m.amount, 0);
  return round2(Math.min(Math.max(raw, 0), Math.max(balance, 0)));
}

/** Tu parte según lo aportado, sin limitar (puede pasar del saldo o ser negativa). */
function poolRawMineAt(c: Computed, account: string, date: string): number {
  const a = c.wb.accounts.find(x => x.name === account);
  if (!a) return 0;
  const share = a.share || 1;
  const moves = c.movements.filter(m => m.account === account && m.date && m.date <= date);
  const base = a.opening + a.archived;
  if (poolMode(a.split) === 'fixed') return round2((base + moves.reduce((t, m) => t + m.amount, 0)) * share);
  return round2(base * share + moves.reduce((t, m) => {
    const side = poolSide(c, account, m);
    return t + (side === 'mine' ? m.amount : side === 'common' ? m.amount * share : 0);
  }, 0));
}

export function poolSummary(c: Computed, account: string, today: string): PoolSummary | null {
  const a = c.wb.accounts.find(x => x.name === account);
  if (!a) return null;
  const share = a.share || 1, year = Number(today.slice(0, 4));
  const moves = c.movements.filter(m => m.account === account && m.date && m.date <= today);
  const balance = round2(a.opening + a.archived + moves.reduce((t, m) => t + m.amount, 0));
  const mine = poolMineAt(c, account, today);
  const raw = poolRawMineAt(c, account, today);
  let cm = 0, co = 0;
  for (const m of moves.filter(x => x.date.startsWith(`${year}-`))) {
    const side = poolSide(c, account, m);
    if (side === 'mine') cm += m.amount; else if (side === 'others') co += m.amount;
  }
  // Pagos al mes: lo que sale en gastos de la cuenta (entero, de todos), en los 12 meses cerrados con datos
  const cur = today.slice(0, 7);
  const months = monthRange(`${year - 1}${cur.slice(4)}`, prevMonth(cur)).filter(m => moves.some(x => x.date.slice(0, 7) <= m));
  const paid = moves.filter(m => m.kind === 'expense' && months.includes(m.date.slice(0, 7))).reduce((t, m) => t - m.amount, 0);
  const monthlyPayments = months.length && paid > 0 ? round2(paid / months.length) : null;
  return {
    account, purpose: accountUse(a).purpose, people: poolPeople(a.people), mode: poolMode(a.split), share,
    balance, mine, others: round2(balance - mine), imbalance: poolMode(a.split) === 'contrib' ? round2(raw - mine) : 0,
    contributed: { year, mine: round2(cm), others: round2(co) },
    monthlyPayments, coverMonths: monthlyPayments ? Math.round((balance / monthlyPayments) * 10) / 10 : null,
  };
}

/** Cuentas apartadas con «tu parte según lo que aporta cada persona». */
const contribPools = (c: Computed) => c.wb.accounts.filter(a => a.inNetWorth && accountUse(a).use === 'reserved' && poolMode(a.split) === 'contrib');

/**
 * Lo que hay que sumar a «saldo × tu %» para tener tu parte de verdad en esas cuentas (para el patrimonio).
 * 0 si no hay ninguna.
 */
export function poolAdjustment(c: Computed, date: string): number {
  return round2(contribPools(c).reduce((t, a) => {
    const raw = (a.opening + a.archived + c.movements.filter(m => m.account === a.name && m.date && m.date <= date).reduce((s, m) => s + m.amount, 0)) * (a.share || 1);
    return t + poolMineAt(c, a.name, date) - raw;
  }, 0));
}
